import * as pc from 'playcanvas';

/** A camera pose on the tour path (PlayCanvas world coordinates). */
export interface PathPose {
    position: { x: number; y: number; z: number };
    rotation: { x: number; y: number; z: number; w: number };
    fov: number;
}

/**
 * Blend factor for one step of `dtSec` that reproduces a per-frame pull of
 * `pullPerFrame` applied at 60 Hz. The live tour damps the camera once per rendered
 * frame; converting the pull to a time-based factor lets an offline renderer at any
 * frame rate land on the same motion. (Live damping is per rendered frame, so on a
 * 120/144 Hz display the live tour is snappier than this 60 Hz reference.)
 */
export function dampingFactor(pullPerFrame: number, dtSec: number): number {
    const pull = Math.min(1, Math.max(0, pullPerFrame));
    const frames = Math.max(0, dtSec) * 60;
    return 1 - Math.pow(1 - pull, frames);
}

/**
 * Time constant (seconds) of the damping: while following a moving target the camera
 * trails it by about `velocity × τ`, and once the target stops the remaining gap
 * shrinks by a factor of e every τ. 0 for a pull that snaps, Infinity for none.
 */
export function dampingTimeConstantSec(pullPerFrame: number): number {
    const pull = Math.min(1, Math.max(0, pullPerFrame));
    if (pull >= 1) return 0;
    if (pull <= 0) return Infinity;
    return -1 / (60 * Math.log(1 - pull));
}

/**
 * Deterministic stand-in for the live tour's camera damping (`updateCameraDamping`):
 * the camera lerps its position and FOV and slerps its rotation a fraction of the way
 * toward the path pose every step. That gives autoplay its soft start from rest, its
 * rounded corners and its slight lag behind the path. The first step seeds the
 * camera on the target, like a tour that starts at rest on the first waypoint.
 */
export class DampedPoseFollower {
    private readonly position = new pc.Vec3();
    private readonly rotation = new pc.Quat();
    private readonly targetPosition = new pc.Vec3();
    private readonly targetRotation = new pc.Quat();
    private fov = 0;
    private seeded = false;

    constructor(private readonly pullPerFrame: number) {}

    /** See dampingTimeConstantSec. */
    get timeConstantSec(): number {
        return dampingTimeConstantSec(this.pullPerFrame);
    }

    reset(): void {
        this.seeded = false;
    }

    step(target: PathPose, dtSec: number): PathPose {
        this.targetPosition.set(target.position.x, target.position.y, target.position.z);
        this.targetRotation.set(target.rotation.x, target.rotation.y, target.rotation.z, target.rotation.w);
        if (!this.seeded) {
            this.position.copy(this.targetPosition);
            this.rotation.copy(this.targetRotation);
            this.fov = target.fov;
            this.seeded = true;
        } else {
            const k = dampingFactor(this.pullPerFrame, dtSec);
            this.position.lerp(this.position, this.targetPosition, k);
            this.rotation.slerp(this.rotation, this.targetRotation, k);
            this.fov += (target.fov - this.fov) * k;
        }
        return {
            position: { x: this.position.x, y: this.position.y, z: this.position.z },
            rotation: { x: this.rotation.x, y: this.rotation.y, z: this.rotation.z, w: this.rotation.w },
            fov: this.fov,
        };
    }
}
